import { FileIcon } from '@/assets/icons'
import ConfirmationModal from '@components/ConfirmationModal'
import PageLoader from '@components/PageLoader'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useDeleteFile, useFiles, useTrip, useUploadFiles } from '@hooks'
import { GetFileType } from '@orbit/api-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import {
	DeleteFileButton,
	EmptyState,
	ErrorContainer,
	FileCard,
	FileGrid,
	FileInfo,
	FileName,
	FilesContent,
	FilesHeader,
	FilesPageContainer,
	FilesTitle,
	FileThumbnail,
	FileTypeIcon,
	HiddenFileInput,
	LoadingContainer,
	UploadButton,
	UploadOverlay,
	UploadSpinner,
	UploadText,
	ViewerBackdrop,
	ViewerButton,
	ViewerCloseButton,
	ViewerContainer,
	ViewerControls,
} from './FilesPage.styles'

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic']
const ACCEPTED_TYPES = [
	...IMAGE_MIME_TYPES,
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'text/plain',
	'text/csv',
].join(',')

const MAX_FILES = 5

function isImage(file: GetFileType): boolean {
	return IMAGE_MIME_TYPES.includes(file.mimeType)
}

function getFileExtension(fileName: string): string {
	const parts = fileName.split('.')
	return parts.length > 1 ? parts[parts.length - 1] : ''
}

const FilesPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: filesData, isLoading: filesLoading } = useFiles(tripId)
	const uploadFiles = useUploadFiles()
	const deleteFile = useDeleteFile()
	const fileInputRef = useRef<HTMLInputElement>(null)

	const [viewerFile, setViewerFile] = useState<GetFileType | null>(null)
	const [deleteTarget, setDeleteTarget] = useState<GetFileType | null>(null)

	useBreadcrumbs(
		trip
			? [
					{ label: 'My Trips', path: '/trips' },
					{ label: trip.name, path: `/trips/${tripId}` },
					{ label: 'Files' },
				]
			: [],
	)

	const handleUploadClick = () => {
		fileInputRef.current?.click()
	}

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files
		if (!selected || selected.length === 0 || !tripId) return

		const fileArray = Array.from(selected)

		if (fileArray.length > MAX_FILES) {
			toast.error(`You can upload up to ${MAX_FILES} files at a time.`)
			if (fileInputRef.current) fileInputRef.current.value = ''
			return
		}

		uploadFiles.mutate(
			{ tripId, files: fileArray, resourceType: 'TRIP' },
			{
				onSuccess: () => {
					toast.success(`${fileArray.length} file${fileArray.length > 1 ? 's' : ''} uploaded`)
				},
				onError: () => {
					toast.error('Failed to upload files. Please try again.')
				},
				onSettled: () => {
					if (fileInputRef.current) fileInputRef.current.value = ''
				},
			},
		)
	}

	const handleFileClick = (file: GetFileType) => {
		if (isImage(file)) {
			setViewerFile(file)
		} else {
			window.open(file.url, '_blank')
		}
	}

	const handleDeleteClick = (e: React.MouseEvent, file: GetFileType) => {
		e.stopPropagation()
		setDeleteTarget(file)
	}

	const handleConfirmDelete = () => {
		if (!deleteTarget || !tripId) return
		deleteFile.mutate(
			{ tripId, fileId: deleteTarget.id },
			{
				onSuccess: () => toast.success('File deleted'),
				onError: () => toast.error('Failed to delete file'),
			},
		)
	}

	const handleDownload = async (file: GetFileType) => {
		try {
			const response = await fetch(file.url)
			const blob = await response.blob()
			const link = document.createElement('a')
			link.href = URL.createObjectURL(blob)
			link.download = file.fileName
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(link.href)
		} catch {
			toast.error('Failed to download file')
		}
	}

	const handleViewerClose = useCallback(() => setViewerFile(null), [])

	useEffect(() => {
		const handleEsc = (e: KeyboardEvent) => {
			if (e.key === 'Escape') handleViewerClose()
		}
		if (viewerFile) {
			document.addEventListener('keydown', handleEsc)
			return () => document.removeEventListener('keydown', handleEsc)
		}
	}, [viewerFile, handleViewerClose])

	if (tripLoading || filesLoading) {
		return (
			<LoadingContainer>
				<PageLoader label="Loading files..." />
			</LoadingContainer>
		)
	}

	if (!trip) {
		return (
			<ErrorContainer>
				<p>Trip not found</p>
			</ErrorContainer>
		)
	}

	const files = filesData?.files || []

	return (
		<FilesPageContainer>
			<FilesContent>
				<FilesHeader>
					<FilesTitle>Files ({files.length})</FilesTitle>
					<UploadButton onClick={handleUploadClick} disabled={uploadFiles.isPending}>
						+ Upload
					</UploadButton>
				</FilesHeader>

				<HiddenFileInput
					ref={fileInputRef}
					type="file"
					multiple
					accept={ACCEPTED_TYPES}
					onChange={handleFileChange}
				/>

				{files.length === 0 ? (
					<EmptyState>
						<FileIcon width={48} height={48} fill="currentColor" />
						<p>No files yet</p>
						<span>Upload photos and documents to share with your trip group</span>
					</EmptyState>
				) : (
					<FileGrid>
						{files.map(file => (
							<FileCard key={file.id} onClick={() => handleFileClick(file)}>
								<FileThumbnail>
									{isImage(file) ? (
										<img src={file.url} alt={file.fileName} loading="lazy" />
									) : (
										<FileTypeIcon>
											<FileIcon width={40} height={40} fill="currentColor" />
											<span>{getFileExtension(file.fileName)}</span>
										</FileTypeIcon>
									)}
								</FileThumbnail>
								<FileInfo>
									<FileName title={file.fileName}>{file.fileName}</FileName>
									<DeleteFileButton
										onClick={e => handleDeleteClick(e, file)}
										title="Delete file"
									>
										✕
									</DeleteFileButton>
								</FileInfo>
							</FileCard>
						))}
					</FileGrid>
				)}
			</FilesContent>

			{uploadFiles.isPending && (
				<UploadOverlay>
					<UploadSpinner />
					<UploadText>Uploading files...</UploadText>
				</UploadOverlay>
			)}

			{viewerFile && (
				<ViewerBackdrop onClick={handleViewerClose}>
					<ViewerContainer onClick={e => e.stopPropagation()}>
						<ViewerCloseButton onClick={handleViewerClose}>✕</ViewerCloseButton>
						<img src={viewerFile.url} alt={viewerFile.fileName} />
						<ViewerControls>
							<ViewerButton onClick={() => handleDownload(viewerFile)}>
								↓ Download
							</ViewerButton>
						</ViewerControls>
					</ViewerContainer>
				</ViewerBackdrop>
			)}

			<ConfirmationModal
				isOpen={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleConfirmDelete}
				title="Delete File"
				message={
					<>
						Are you sure you want to delete <strong>{deleteTarget?.fileName}</strong>?
						This cannot be undone.
					</>
				}
				confirmText="Delete"
				variant="danger"
			/>
		</FilesPageContainer>
	)
}

export default FilesPage
